import { HACKLE_DEVTOOLS_STORAGE_KEY, useIsDevEnvironment } from "goi_common";
import { QUESTION_CONTEXT_EXPERIMENT_KEYS } from "@/utils/questionContextExperiment";

/**
 * 비용 되짚기 화면의 로그인 이후 화면 마킹.
 *
 * 두 경로가 이 마킹을 공유한다. 로그인 이후 화면(수령 방식 로그인, 비용 구성 카드)이
 * 같기 때문이다.
 *
 * | 경로 | 유입/니즈 | 마킹 시점 | 이 마킹을 읽는 화면 |
 * |---|---|---|---|
 * | **채택분** | /match/request 첫문항 비용 + at-needs | 로그인 화면 진입(실험 아님) | 견적 결과 |
 * | #347, #348 | /match/parlor-requests 견적 폼 | 로그인 화면(광고 347 / 오가닉 348) | 견적 결과 |
 *
 * 한 세션이 두 경로를 거치면(예: /match/request 로 제출한 뒤 같은 탭에서 parlor-requests 로
 * 재제출) 뒤쪽 실험의 변형과 무관하게 결과 화면은 되짚기로 남는다. 마킹의 뜻이 "이 세션은
 * 비용 되짚기" 이고 ITT 로 지우지 않기 때문이다(아래 참고). 결과 화면 지표는 실험별 노출자
 * 기준으로 쪼개 볼 것.
 *
 * 키 이름이 `cost_needs_login_b` 로 남은 것은 #347 이 진행 중이라서다. 이름을 바꾸면
 * 이미 마킹을 들고 있는 세션이 현행 화면으로 떨어진다. `_b` 는 이제 변형이 아니라
 * 과거 실험명의 흔적이다.
 *
 * 되짚기 화면은 로그인 화면 하나로 끝나지 않는다. 로그인에서 "비용을 안내해 드릴게요" 라고
 * 약속했으면 그 다음 화면인 견적 결과에서도 비용 이야기로 받아야 약속이 이어진다. 그래서
 * 결과 화면까지 포함한다.
 *
 * ## 왜 결과 화면에서 실험을 다시 부르지 않는가
 *
 * 두 가지 이유다.
 *
 * 1. 결과 화면은 **로그인 이후**다. `onLoginSuccess` 가 `HackleClient.setUser()` 로
 *    익명 사용자를 회원으로 바꾼다. 그 뒤에 `useCustomVariation` 을 다시 부르면 버킷팅
 *    기준이 로그인 전과 같다고 보장할 수 없다. 같은 사람이 A 를 보고 들어와 B 결과 화면을
 *    볼 수 있다.
 * 2. 다시 부르면 Hackle 에 노출이 한 번 더 기록된다.
 *
 * 그래서 할당 시점(로그인 화면)에 세션에 남기고 결과 화면은 읽기만 한다.
 * `utils/parlorNeedsRollbackLogin.ts` 와 같은 구조이고, 그쪽 주석의 근거도 같다.
 *
 * ## 마킹을 지우지 않는 이유
 *
 * "이 세션에서 B 에 할당된 적이 있다" 가 이 값의 의미다. 노출된 실험군을 끝까지 따라가는
 * ITT 이므로 중간에 다른 경로를 거쳐도 B안은 유지돼야 한다. 탭을 닫으면 사라진다.
 */
const COST_NEEDS_B_KEY = "cost_needs_login_b";

/**
 * 되짚기 화면을 보여주는 순간 마킹.
 *
 * 실험 경로(#347, #348)는 `HackleExperimentContainer` 의 `onAssigned` 에서,
 * 채택 경로(첫문항 비용 + at-needs)는 `CostNeedsLoginScreen` 의 effect 에서 부른다.
 */
export const markCostNeedsLoginB = () => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(COST_NEEDS_B_KEY, "1");
  } catch {
    // 사파리 프라이빗 등 쓰기 실패. 마킹이 없으면 결과 화면은 현행(A)으로 흐른다
  }
};

/**
 * 이 마킹이 대변하는 실험. dev 오버라이드를 읽을 때 쓴다
 * (347, 348 = 장례식장 견적).
 *
 * **마킹을 심는 실험을 추가하면 여기에도 키를 넣을 것.** 빠뜨리면 devtools 로 A 를 강제해도
 * 이 목록에 없어서 오버라이드가 잡히지 않고, 앞서 남은 마킹만 읽혀 결과 화면이 계속 B 로 간다
 * (OX 퀴즈 자리에 비용 구성 카드가 뜬다). 347, 348 에서 실제로 한 번 걸렸다.
 */
const COST_NEEDS_EXPERIMENT_KEYS = [347, 348, ...QUESTION_CONTEXT_EXPERIMENT_KEYS];

/**
 * GoiDevTools 로 강제한 변형. dev 환경이 아니거나 값이 없으면 `null`.
 *
 * `COST_NEEDS_EXPERIMENT_KEYS` 중 하나라도 강제돼 있으면 그 값을 따르고, B 가 하나라도
 * 있으면 B 로 본다. 한 사람은 그중 한 실험에만 들어가므로(니즈와 유입이 갈린다) 여럿을
 * 같이 볼 일이 없다.
 */
const devToolsForcedB = (): boolean | null => {
  try {
    const raw = window.localStorage.getItem(HACKLE_DEVTOOLS_STORAGE_KEY);
    if (!raw) return null;

    const variations: Record<string, string> = JSON.parse(raw);
    const forced = COST_NEEDS_EXPERIMENT_KEYS.map((key) => variations[key]).filter(
      (v) => v === "A" || v === "B",
    );
    return forced.length ? forced.includes("B") : null;
  } catch {
    return null;
  }
};

/**
 * 비용 니즈 B 할당자 여부. sessionStorage 는 클라 전용이라 렌더 시 읽는다.
 *
 * 마킹이 없으면 `false` = 현행 화면이다. 쓰기 실패나 탭 교체에서 안전한 쪽으로 떨어진다.
 *
 * ## dev 에서는 GoiDevTools 오버라이드가 마킹을 이긴다
 *
 * 마킹은 로그인 화면에서 B 에 할당된 순간 심기고 지워지지 않는다(ITT). 그래서 devtools 로
 * 변형을 A 로 바꿔도 결과 화면은 계속 B 라 QA 가 막힌다. 실제로 한 번 걸렸다.
 * dev, preview 에서만 오버라이드를 먼저 보고, 프로덕션 동작은 그대로 마킹만 본다.
 *
 * 훅인 이유는 `useIsDevEnvironment` 를 재사용하기 위해서다. 판정 로직을 복제하면
 * 그쪽이 바뀔 때 여기만 남는다.
 */
export const useIsCostNeedsLoginB = () => {
  const isDevEnvironment = useIsDevEnvironment();

  if (typeof window === "undefined") return false;

  if (isDevEnvironment) {
    const forced = devToolsForcedB();
    if (forced !== null) return forced;
  }

  return sessionStorage.getItem(COST_NEEDS_B_KEY) !== null;
};
